/**
 * Option values of a project assignment or pack item: the effective values
 * shown in the editor, and the sparse overrides sent on save.
 */

import type { Feature } from '../schemas/admin.schemas';

type Options = Record<string, unknown>;

/** Effective options: the feature's defaults with the saved options on top. */
export function buildValues(feature: Feature, saved: Options | null | undefined): Options {
  return { ...feature.default_options, ...(saved ?? {}) };
}

/** True for a language pack: lines published at package-version tags. */
function pinsLine(feature: Feature): feature is Feature & { variant_key: string } {
  return Boolean(feature.variant_key) && feature.retired_variants != null;
}

/**
 * Only the values that differ from the defaults, or null when none do. A
 * language pack's line is always kept: the server keeps the saved line when
 * the key is omitted, so a switch back to the default line must send it.
 */
export function computeSparseOverrides(values: Options, feature: Feature): Options | null {
  const overrides: Options = {};
  for (const [key, value] of Object.entries(values)) {
    const keep = pinsLine(feature) && key === feature.variant_key;
    if (keep || value !== feature.default_options[key]) overrides[key] = value;
  }
  return Object.keys(overrides).length > 0 ? overrides : null;
}

/** True when an admin-only option has neither a saved value nor a default. */
export function hasUnconfiguredOptions(feature: Feature, saved: Options | null | undefined): boolean {
  for (const [key, schema] of Object.entries(feature.option_schema)) {
    if (!schema.admin_only) continue;
    if (!(saved?.[key] ?? feature.default_options[key])) return true;
  }
  return false;
}
